import { createElement, type ReactNode, type SVGAttributes } from 'react';

import type { DatabaseViewType } from '@couplespace/core/databases';

const iconProps: SVGAttributes<SVGSVGElement> = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 16 16',
  fill: 'none',
  width: 16,
  height: 16,
  'aria-hidden': true,
  className: 'cs-menu-icon',
};

const stroke = {
  stroke: 'currentColor',
  strokeWidth: 1.35,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function menuLabelWithIcon(text: string, icon: ReactNode): ReactNode {
  return createElement(
    'span',
    { className: 'cs-menu-item-content' },
    createElement('span', { className: 'cs-menu-item-icon' }, icon),
    createElement('span', { className: 'cs-menu-item-label' }, text),
  );
}

export function IconViewTable() {
  return createElement(
    'svg',
    iconProps,
    createElement('rect', { ...stroke, x: 3.25, y: 3.25, width: 4.25, height: 4.25, rx: 0.5 }),
    createElement('rect', { ...stroke, x: 8.5, y: 3.25, width: 4.25, height: 4.25, rx: 0.5 }),
    createElement('rect', { ...stroke, x: 3.25, y: 8.5, width: 4.25, height: 4.25, rx: 0.5 }),
    createElement('rect', { ...stroke, x: 8.5, y: 8.5, width: 4.25, height: 4.25, rx: 0.5 }),
  );
}

export function IconViewList() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M3.5 5h1.25M6.25 5h6.25M3.5 8h1.25M6.25 8h6.25M3.5 11h1.25M6.25 11h6.25' }),
    createElement('circle', { ...stroke, cx: 4.125, cy: 5, r: 0.6, fill: 'currentColor' }),
    createElement('circle', { ...stroke, cx: 4.125, cy: 8, r: 0.6, fill: 'currentColor' }),
    createElement('circle', { ...stroke, cx: 4.125, cy: 11, r: 0.6, fill: 'currentColor' }),
  );
}

export function IconViewBoard() {
  return createElement(
    'svg',
    iconProps,
    createElement('rect', { ...stroke, x: 3.25, y: 3.25, width: 2.75, height: 9.5, rx: 0.5 }),
    createElement('rect', { ...stroke, x: 6.625, y: 3.25, width: 2.75, height: 9.5, rx: 0.5 }),
    createElement('rect', { ...stroke, x: 10, y: 3.25, width: 2.75, height: 9.5, rx: 0.5 }),
  );
}

export function IconViewCalendar() {
  return createElement(
    'svg',
    iconProps,
    createElement('rect', { ...stroke, x: 3.25, y: 4.25, width: 9.5, height: 8.5, rx: 0.75 }),
    createElement('path', { ...stroke, d: 'M3.25 7.25h9.5M6 3.25v2M10 3.25v2' }),
  );
}

export function IconViewTimeline() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M2.75 4.5h2.5M2.75 8h5.5M2.75 11.5h8.5' }),
    createElement('circle', { ...stroke, cx: 6.75, cy: 4.5, r: 0.85 }),
    createElement('circle', { ...stroke, cx: 9.75, cy: 8, r: 0.85 }),
    createElement('circle', { ...stroke, cx: 12.75, cy: 11.5, r: 0.85 }),
  );
}

export function iconForViewType(type: DatabaseViewType): ReactNode {
  switch (type) {
    case 'board':
      return IconViewBoard();
    case 'list':
      return IconViewList();
    case 'calendar':
      return IconViewCalendar();
    case 'timeline':
      return IconViewTimeline();
    case 'table':
    default:
      return IconViewTable();
  }
}

export function IconSearch() {
  return createElement(
    'svg',
    iconProps,
    createElement('circle', { ...stroke, cx: 7.25, cy: 7.25, r: 3.25 }),
    createElement('path', { ...stroke, d: 'M9.75 9.75 12.75 12.75' }),
  );
}

export function IconFilter() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M2.75 4.25h10.5M4.25 8h7.5M6.25 11.75h3.5',
    }),
  );
}

/** ArrowUpDown: left column ↓, right column ↑ */
export function IconSort() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M5.5 3.25v8.5' }),
    createElement('path', { ...stroke, d: 'M3.75 9.5 5.5 11.75 7.25 9.5' }),
    createElement('path', { ...stroke, d: 'M10.5 12.75V4.25' }),
    createElement('path', { ...stroke, d: 'M8.75 6 10.5 4.25 12.25 6' }),
  );
}

export function IconGroup() {
  return createElement(
    'svg',
    iconProps,
    createElement('rect', { ...stroke, x: 2.75, y: 3.25, width: 10.5, height: 3, rx: 0.5 }),
    createElement('rect', { ...stroke, x: 2.75, y: 7.5, width: 10.5, height: 3, rx: 0.5 }),
    createElement('rect', { ...stroke, x: 2.75, y: 11.75, width: 10.5, height: 3, rx: 0.5 }),
  );
}

export function IconMenu() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M3 5h10M3 8h10M3 11h10' }),
  );
}

export function IconSettings() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M3.5 4.75h3.25M9.25 4.75h3.25M3.5 8h3.25M9.25 8h3.25M3.5 11.25h3.25M9.25 11.25h3.25',
    }),
    createElement('circle', { ...stroke, cx: 5.125, cy: 4.75, r: 0.85 }),
    createElement('circle', { ...stroke, cx: 10.875, cy: 8, r: 0.85 }),
    createElement('circle', { ...stroke, cx: 5.125, cy: 11.25, r: 0.85 }),
  );
}

export function IconRename() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M9.5 3.25 12.75 6.5l-6 6H3.25v-3.25l6-6Z',
    }),
  );
}

/** Horizontal swap arrows (change type). */
export function IconChangeType() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M3.25 5.5H9.75M7.75 3.75 9.75 5.5 7.75 7.25' }),
    createElement('path', { ...stroke, d: 'M12.75 10.5H6.25M8.25 8.75 6.25 10.5 8.25 12.25' }),
  );
}

/** Sliders (edit property). */
export function IconEditProperty() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M3.5 4.75h3.25M9.25 4.75h3.25M3.5 8h3.25M9.25 8h3.25M3.5 11.25h3.25M9.25 11.25h3.25',
    }),
    createElement('circle', { ...stroke, cx: 5.125, cy: 4.75, r: 0.85 }),
    createElement('circle', { ...stroke, cx: 10.875, cy: 8, r: 0.85 }),
    createElement('circle', { ...stroke, cx: 5.125, cy: 11.25, r: 0.85 }),
  );
}

export function IconMoveLeft() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M9.5 4.25 5.5 8l4 3.75M5.5 8h7.25' }),
  );
}

export function IconMoveRight() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M6.5 4.25 10.5 8l-4 3.75M10.5 8H3.25' }),
  );
}

export function IconHide() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M2.75 8s2.25-3.5 5.25-3.5 5.25 3.5 5.25 3.5-2.25 3.5-5.25 3.5S2.75 8 2.75 8Z',
    }),
    createElement('circle', { ...stroke, cx: 8, cy: 8, r: 1.75 }),
    createElement('path', { ...stroke, d: 'M3.25 3.25 12.75 12.75' }),
  );
}

/** Sigma (summation / calculate). */
export function IconCalculate() {
  return createElement(
    'svg',
    {
      ...iconProps,
      fill: 'currentColor',
      stroke: 'none',
    },
    createElement(
      'text',
      {
        x: 8,
        y: 11.5,
        textAnchor: 'middle',
        dominantBaseline: 'middle',
        fontSize: 13,
        fontWeight: 600,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
      },
      'Σ',
    ),
  );
}

export function IconDelete() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', {
      ...stroke,
      d: 'M3.25 4.75h9.5M5.75 4.75V3.75a1 1 0 0 1 1-1h2.5a1 1 0 0 1 1 1v1M6.25 7.25v4M9.75 7.25v4',
    }),
    createElement('path', { ...stroke, d: 'M4.25 4.75v8.5a1 1 0 0 0 1 1h5.5a1 1 0 0 0 1-1v-8.5' }),
  );
}

export function IconSortAsc() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M8 3.25v9.5M5.5 5.5 8 3.25 10.5 5.5' }),
  );
}

export function IconSortDesc() {
  return createElement(
    'svg',
    iconProps,
    createElement('path', { ...stroke, d: 'M8 3.25v9.5M5.5 10.5 8 12.75 10.5 10.5' }),
  );
}
